// src/components/ImageGallery.js
import React, { useState, useEffect, useRef } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { X, ChevronLeft, ChevronRight, Image as ImageIcon, ZoomIn, Camera } from 'lucide-react';

const ImageGallery = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const scrollRef = useRef(null);

  // Fetch images
  useEffect(() => {
    const fetchImages = async () => {
      try {
        const q = query(collection(db, 'galleryImages'), orderBy('order', 'asc'));
        const snap = await getDocs(q);
        const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setImages(data.filter(img => img.active !== false));
      } catch (error) {
        console.error('Error fetching gallery:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchImages();
  }, []);

  // ✅ Auto-slide every 3 seconds (Reels style)
  useEffect(() => {
    if (images.length <= 1 || isPaused || lightboxIndex !== null) return;

    const interval = setInterval(() => {
      if (scrollRef.current) {
        const cardWidth = 280 + 16; // card width + gap
        const currentScroll = scrollRef.current.scrollLeft;
        const maxScroll = scrollRef.current.scrollWidth - scrollRef.current.clientWidth;

        if (currentScroll >= maxScroll - 10) {
          // End par pahunch gaya → wapas start par
          scrollRef.current.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          // Aage slide
          scrollRef.current.scrollBy({ left: cardWidth, behavior: 'smooth' });
        }
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [images, isPaused, lightboxIndex]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (lightboxIndex === null) return;
    const handler = (e) => {
      if (e.key === 'Escape') setLightboxIndex(null);
      if (e.key === 'ArrowRight') setLightboxIndex(prev => (prev + 1) % images.length);
      if (e.key === 'ArrowLeft') setLightboxIndex(prev => (prev - 1 + images.length) % images.length);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxIndex, images.length]);

  // Manual scroll (arrows)
  const scroll = (direction) => {
    if (!scrollRef.current) return;
    const amount = 296;
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -amount : amount,
      behavior: 'smooth',
    });
  };

  if (loading) {
    return (
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-10">
        <div className="flex gap-4 overflow-hidden">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="shrink-0 w-64 aspect-[9/16] bg-pink-100 rounded-2xl animate-pulse"></div>
          ))}
        </div>
      </section>
    );
  }

  if (images.length === 0) return null;

  return (
    <>
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12 anim-fadeInUp">

        {/* Header */}
        <div className="text-center mb-6 md:mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-3"
            style={{ backgroundColor: 'rgba(220, 130, 154, 0.1)', color: '#DC829A' }}>
            <Camera size={14} />
            <span className="text-xs font-bold uppercase tracking-wider">Gallery</span>
          </div>
          <h2 className="text-2xl md:text-4xl font-bold mb-2" style={{ color: '#9B3F5B' }}>
            Our Gallery
          </h2>
          <p className="text-sm md:text-base" style={{ color: '#DC829A' }}>
            Swipe to explore our organic journey
          </p>
        </div>

        {/* Reels Style Slider */}
        <div
          className="relative"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          onTouchStart={() => setIsPaused(true)}
          onTouchEnd={() => setTimeout(() => setIsPaused(false), 5000)}
        >

          {/* Left Arrow */}
          {images.length > 2 && (
            <button
              onClick={() => scroll('left')}
              className="hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 items-center justify-center rounded-full shadow-xl btn-2d transition"
              style={{ backgroundColor: '#F5AEC0', color: '#FFFFFF' }}
              aria-label="Previous"
            >
              <ChevronLeft size={24} />
            </button>
          )}

          {/* Scroll Container */}
          <div
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto scroll-smooth pb-4 snap-x snap-mandatory"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
          >
            {images.map((img, i) => (
              <button
                key={img.id}
                onClick={() => setLightboxIndex(i)}
                className="relative shrink-0 w-56 sm:w-64 md:w-72 aspect-[9/16] rounded-2xl overflow-hidden shadow-xl group cursor-pointer snap-start anim-scaleIn transition-all duration-500 hover:scale-[1.02]"
                style={{
                  animationDelay: `${i * 80}ms`,
                  border: `2px solid #F5AEC0`,
                }}
              >
                {/* Image */}
                <img
                  src={img.image}
                  alt={img.title || `Gallery ${i + 1}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[1500ms]"
                />

                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>

                {/* Zoom icon on hover */}
                <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-all duration-300">
                  <ZoomIn size={40} className="text-white opacity-0 group-hover:opacity-100 transition-all duration-300 scale-50 group-hover:scale-100 drop-shadow-lg" />
                </div>

                {/* Title */}
                {img.title && (
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <p className="text-white text-sm md:text-base font-bold text-left line-clamp-2 drop-shadow-lg">
                      {img.title}
                    </p>
                  </div>
                )}

                {/* Index Badge */}
                <div className="absolute top-3 right-3 px-2 py-1 rounded-full text-xs font-bold text-white"
                  style={{ backgroundColor: 'rgba(220, 130, 154, 0.9)' }}>
                  {i + 1}/{images.length}
                </div>
              </button>
            ))}
          </div>

          {/* Right Arrow */}
          {images.length > 2 && (
            <button
              onClick={() => scroll('right')}
              className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 items-center justify-center rounded-full shadow-xl btn-2d transition"
              style={{ backgroundColor: '#F5AEC0', color: '#FFFFFF' }}
              aria-label="Next"
            >
              <ChevronRight size={24} />
            </button>
          )}

          {/* Mobile Swipe Hint */}
          {images.length > 2 && (
            <p className="text-center text-xs mt-3 md:hidden" style={{ color: '#DC829A' }}>
              ← Swipe to see more →
            </p>
          )}

          {/* Auto-slide Indicator */}
          {images.length > 1 && (
            <div className="flex items-center justify-center gap-1 mt-4">
              <div className={`w-2 h-2 rounded-full ${!isPaused ? 'animate-pulse' : ''}`}
                style={{ backgroundColor: '#DC829A' }}></div>
              <span className="text-[10px] font-semibold" style={{ color: '#DC829A' }}>
                {isPaused ? 'Paused' : 'Auto-playing'}
              </span>
            </div>
          )}
        </div>
      </section>

      {/* ================= LIGHTBOX ================= */}
      {lightboxIndex !== null && images[lightboxIndex] && (
        <div
          className="fixed inset-0 z-[100] bg-black bg-opacity-95 flex items-center justify-center p-4 anim-fadeIn"
          onClick={() => setLightboxIndex(null)}
        >
          {/* Close */}
          <button
            onClick={() => setLightboxIndex(null)}
            className="absolute top-4 right-4 md:top-6 md:right-6 p-2 md:p-3 rounded-full bg-white/10 hover:bg-white/20 text-white backdrop-blur-md transition z-10"
          >
            <X size={24} />
          </button>

          {/* Prev */}
          {images.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIndex(prev => (prev - 1 + images.length) % images.length);
              }}
              className="absolute left-3 md:left-6 p-2 md:p-3 rounded-full bg-white/10 hover:bg-white/20 text-white backdrop-blur-md transition z-10 btn-2d"
            >
              <ChevronLeft size={24} className="md:w-8 md:h-8" />
            </button>
          )}

          {/* Image */}
          <div
            className="max-w-5xl max-h-[85vh] w-full flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={images[lightboxIndex].image}
              alt={images[lightboxIndex].title || 'Gallery'}
              className="max-h-[80vh] max-w-full object-contain rounded-xl shadow-2xl anim-scaleIn"
            />
            {images[lightboxIndex].title && (
              <p className="text-white text-center mt-4 text-sm md:text-base font-semibold">
                {images[lightboxIndex].title}
              </p>
            )}
            <p className="text-white/60 text-xs mt-1">
              {lightboxIndex + 1} / {images.length}
            </p>
          </div>

          {/* Next */}
          {images.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIndex(prev => (prev + 1) % images.length);
              }}
              className="absolute right-3 md:right-6 p-2 md:p-3 rounded-full bg-white/10 hover:bg-white/20 text-white backdrop-blur-md transition z-10 btn-2d"
            >
              <ChevronRight size={24} className="md:w-8 md:h-8" />
            </button>
          )}
        </div>
      )}
    </>
  );
};

export default ImageGallery;